import { useRef, useState } from 'react';
import { LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { OrderItem } from '@mews-commander/data-access';
import { FloatingPanel } from '@mews/b2b-ui';
import { OptimusProvider } from '@mews-commander/ui';
import { Button } from '@optimus-web/core';
import { useChangeItemSuccessPopup } from '../hooks/useChangeItemSuccessPopup';
import { EditItemDialogContent } from './EditItemDialogContent';

interface Props {
    orderItem: OrderItem;
    active: boolean;
    onClose: () => void;
}

interface FormRef {
    submit: () => Promise<void>;
}

export const EditItemDialog = ({ orderItem, active, onClose }: Props) => {
    const [loading, setLoading] = useState(false);

    const showChangeItemSuccessPopup = useChangeItemSuccessPopup({
        title: TextKey.ItemUpdatedSuccess,
        orderItem,
    });

    const handleSubmit = async () => {
        try {
            setLoading(true);
            await formRef.current?.submit();
        } finally {
            setLoading(false);
        }
    };

    const formRef = useRef<FormRef | null>(null);

    if (!orderItem && active) {
        return null;
    }

    return (
        <OptimusProvider density="spacious">
            <FloatingPanel
                active={active}
                loading={loading}
                onClose={onClose}
                title={lt('EditItem')}
                footer={{
                    primary: (
                        <Button variant="primary" size="medium" onClick={handleSubmit} type="submit">
                            <LocalizedText textKey={TextKey.Update} />
                        </Button>
                    ),
                }}
            >
                <EditItemDialogContent
                    orderItem={orderItem}
                    closeDialog={onClose}
                    ref={formRef}
                    onSubmitSuccess={() => {
                        showChangeItemSuccessPopup();
                    }}
                />
            </FloatingPanel>
        </OptimusProvider>
    );
};
